import styled from "@emotion/styled";
import { BottomSheet } from "react-spring-bottom-sheet";

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  z-index: 1300;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.cores.overlay};

  @media (max-width: 768px) {
    align-items: flex-end;
  }
`;

export const Painel = styled.div`
  background-color: ${({ theme }) => theme.cores.surface2};
  border-radius: 24px;
  width: 800px;
  max-width: 96vw;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 24px 24px 0 0;
    max-height: 88vh;
  }
`;

export const Cabecalho = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 19px 16px 19px;
  flex-shrink: 0;
`;

export const Titulo = styled.h2`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.cores.textoModal};
  letter-spacing: 0.15px;
`;

export const BotaoFechar = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.15s;
  color: ${({ theme }) => theme.cores.semanticDanger2};

  &:hover {
    background-color: ${({ theme }) => `${theme.cores.semanticDanger2}18`};
  }
`;

// Confirmação de ação destrutiva (Cancelamento/Inutilização/CCE) - camada própria, renderizada por
// cima do Painel principal (não usa o ModalConfirmarExclusao compartilhado porque ele faz
// createPortal pro body via reactjs-popup com z-index fixo em 999, menor que os 1300 do Overlay
// abaixo - ficaria escondido atrás do próprio ModalGerenciadorFiscal). z-index maior + ordem no
// DOM (depois do Painel principal) garantem que fica na frente dele.
export const OverlayConfirmacao = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  z-index: 1400;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.cores.overlay};
`;

export const PainelConfirmacao = styled.div`
  background-color: ${({ theme }) => theme.cores.surface2};
  border-radius: 24px;
  width: 420px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.32);
`;

export const MensagemConfirmacao = styled.p`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.cores.textoModal};
  padding: 24px;
`;

export const Corpo = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 19px 20px 19px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.cores.surfaceActionActivePrimary};
    border-radius: 5px;
  }
`;

/* ── Seletor de Modelo Fiscal (NF-e / NFC-e) ── */

export const LinhaModelos = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const BotaoModelo = styled.button`
  height: 36px;
  min-width: 80px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1.5px solid ${({ theme }) => theme.cores.surfaceActionActivePrimary};
  background-color: ${({ $ativo, theme }) =>
    $ativo ? theme.cores.surfaceActionActivePrimary : "transparent"};
  color: ${({ $ativo, theme }) =>
    $ativo ? theme.cores.textActionPrimary : theme.cores.surfaceActionActivePrimary};
  font-family: "Source Sans Pro", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background-color: ${({ $ativo, theme }) =>
      $ativo
        ? theme.cores.surfaceActionActivePrimary
        : `${theme.cores.surfaceActionActivePrimary}18`};
  }
`;

/* ── Radio Buttons (horizontal) ── */

export const LinhaRadios = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex-wrap: wrap;
`;

export const ItemRadio = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  outline: none;
  ${({ $desabilitado }) => $desabilitado && "opacity: 0.4; cursor: not-allowed;"}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.cores.surfaceActionActivePrimary};
    outline-offset: 2px;
  }
`;

export const RadioCircle = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.cores.surfaceActionActivePrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const RadioDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.cores.surfaceActionActivePrimary};
  display: ${({ $visivel }) => ($visivel ? "block" : "none")};
`;

export const TextoRadio = styled.span`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.cores.textoSimples};
  user-select: none;
  white-space: nowrap;
`;

/* ── Formulário ── */

export const SecaoFormulario = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const LinhaFormulario = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 540px) {
    flex-direction: column;
  }
`;

export const CampoWrapper = styled.div`
  flex: ${({ $flex }) => $flex || 1};
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/* ── Área de retorno da SEFAZ ── */

export const AreaRetorno = styled.div`
  border-radius: 16px;
  background-color: ${({ theme }) => theme.cores.corBGAccordion};
  padding: 14px 16px;
  margin-bottom: 8px;
  min-height: 90px;
  max-height: 180px;
  overflow-y: auto;
  font-family: "Source Sans Pro", monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.cores.textoSimples};
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.5;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.cores.surfaceActionActivePrimary};
    border-radius: 5px;
  }
`;

export const PlaceholderRetorno = styled.p`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.cores.textoSimples};
  opacity: 0.45;
  font-style: italic;
`;

export const LabelSecao = styled.p`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.cores.neutral40};
  margin-top: 12px;
  margin-bottom: 2px;
`;

/* ── Rodapé / Botões ── */

export const Rodape = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 14px 19px;
  border-top: 1px solid ${({ theme }) => theme.cores.stroke};
  flex-shrink: 0;
`;

export const BotaoCancelar = styled.button`
  height: 48px;
  min-width: 152px;
  padding: 0 24px;
  border-radius: 8px;
  border: 1.5px solid ${({ theme }) => theme.cores.surfaceActionActivePrimary};
  background: transparent;
  color: ${({ theme }) => theme.cores.surfaceActionActivePrimary};
  font-family: "Source Sans Pro", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;

  /* :focus (não :focus-visible) de propósito - o navegador só mostra o outline nativo em foco por
     teclado/Enter, não por clique de mouse, o que fazia o botão parecer "selecionado" só num dos
     dois casos. Estilo explícito garante a mesma aparência nos dois. */
  &:hover,
  &:focus {
    background-color: ${({ theme }) => `${theme.cores.surfaceActionActivePrimary}12`};
    outline: none;
  }
`;

// $perigo: variante vermelha usada na confirmação de Cancelamento/Inutilização (ações
// irreversíveis na SEFAZ) - mesmo botão, só troca a cor de fundo.
export const BotaoExecutar = styled.button`
  height: 48px;
  min-width: 152px;
  padding: 0 24px;
  border-radius: 8px;
  border: none;
  background-color: ${({ theme, $perigo }) =>
    $perigo ? theme.cores.semanticDanger2 : theme.cores.surfaceActionActivePrimary};
  color: ${({ theme }) => theme.cores.textActionPrimary};
  font-family: "Source Sans Pro", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const MensagemErro = styled.p`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 11px;
  color: ${({ theme }) => theme.cores.semanticDanger2};
  margin-top: 2px;
`;

export const TextoAjuda = styled.p`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 11px;
  color: ${({ theme }) => theme.cores.textoSimples};
  opacity: 0.6;
  margin-top: 2px;
`;

export const BottomSheetCustomizada = styled(BottomSheet)`
  --rsbs-bg: ${({ theme }) => theme.cores.surface2};
`;
